import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import getTargetValue from 'services/forms/getTargetValue';
import IGlassArea from 'components/interfaces/IGlassArea';
import IRouteParams from 'interfaces/IRouteParams';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import store from 'store/store';
import translations from 'services/translations/translations';
import {
  setRawWall,
  setWall,
  setWallIdentifier,
  setWallMetaData,
} from 'store/selectedWall/actions';

import actionTypes from '../types/actionTypes';
import AddConnectionByWallLine from '../rightBar/src/AddConnectionByWallLine';
import AddConnectionByDelta from './AddConnectionByDelta';
import AddWall from './AddWall';
import AddWall2Connections from './AddWall2Connections';
import ChangeGlassMeetingSign from './ChangeGlassMeetingSign';
import Downloads from './Downloads';
import getDefaultWall from '../services/getDefaultWall';
import getExistingEditableWall from '../services/getExistingEditableWall';
import getOptions from '../services/getOptions';
import getSelectedWallIdentifier from '../services/getSelectedWallIdentifier';
import GlassWallModal from '../modals/GlassWallModal';
import isDot from './services/isDot';
import isGlassWall from './services/isGlassWall';
import isWall from './services/isWall';
import loadGlassAreas from '../modals/src/glassWall/glassAreas/loadGlassAreas';
import loadWallMetaData from '../services/loadWallMetaData';
import ReAttachConnections from './ReAttachConnections';
import { THREE_D } from '../types/dimensions';

import 'style/layout/navbar/PropertiesBar.css';

const PropertiesBar = ({
  dimensionStatus,
  hasWritePermissions,
  propertiesBarOpen,
}: {
  dimensionStatus: string;
  hasWritePermissions: boolean;
  propertiesBarOpen: boolean;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const selectedWall = useSelector((state: any) => state.selectedWall.raw);
  const wall: any = store.getState().selectedWall.properties;
  const wallMetaData = useSelector((state: any) => state.selectedWall.metaData);
  const [glassAreas, setGlassAreas] = useState<IGlassArea[] | null>(null);
  const { t } = useTranslation([translations.common]);
  const [actionType, setActionType] = useState(actionTypes.addWall);
  const [selectedDot, setSelectedDot] = useState<any | undefined>(undefined);
  const selectedWallRef = useRef(selectedWall);
  const [wallCoordinates, setWallCoordinates] = useState({
    x: 0,
    y: 0,
  });
  const [actionOptions, setActionOptions] = useState<JSX.Element[]>([]);
  const { configurationId } = useParams<IRouteParams>();

  const handleActionType = (e: any) => {
    setActionType(getTargetValue(e));
  };

  const restartFields = () => {
    dispatch(setRawWall(undefined));
    setSelectedDot(undefined);
    selectedWallRef.current = undefined;
  };

  const keydownEvent = (e: any) => {
    if (e.key === 'Escape') {
      restartFields();
    }
  };

  const triggerEscape = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
  };

  const closeModal = () => {
    triggerEscape();
    restartFields();
  };

  const closeModalNotSaved = () => {
    triggerEscape();
    dispatch(setRawWall(undefined));
    dispatch(setWall(getDefaultWall()));
    setSelectedDot(undefined);
    selectedWallRef.current = undefined;
  };

  const mousedownEvent = (e: any) => {
    const pickedObject = window.GetCurrentPickedObject();

    if (isDot(pickedObject)) {
      setSelectedDot(pickedObject);
    }

    if (isWall(pickedObject)) {
      if (pickedObject.properties === undefined) {
        return;
      }

      if (selectedWallRef.current !== undefined) {
        if (pickedObject.uuid === selectedWallRef.current.uuid) {
          return;
        }
      }

      if (selectedWallRef.current === undefined) {
        setWallCoordinates({
          ...wallCoordinates,
          x: e.x,
          y: e.y,
        });
      }

      setGlassAreas(null);
      selectedWallRef.current = pickedObject;
      dispatch(setRawWall(pickedObject));
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', keydownEvent);
    document.addEventListener('mousedown', mousedownEvent);

    return () => {
      document.removeEventListener('keydown', keydownEvent);
      document.removeEventListener('mousedown', mousedownEvent);
    };
  }, []);

  useEffect(() => {
    if (selectedWall !== undefined) {
      const is3D = dimensionStatus === THREE_D;

      if (is3D && !isGlassWall(selectedWall)) {
        triggerEscape();
        return;
      }

      const selectedWallIdentifier = getSelectedWallIdentifier(selectedWall);

      const existingWall = getExistingEditableWall(selectedWall);
      dispatch(setWall(existingWall));
      dispatch(setWallIdentifier(selectedWallIdentifier));
      dispatch(
        setWallMetaData(
          loadWallMetaData(selectedWallIdentifier, existingWall, is3D)
        )
      );
    }
  }, [selectedWall]);

  useEffect(() => {
    if (glassAreas === null && dimensionStatus === THREE_D) {
      setGlassAreas(loadGlassAreas(selectedWall, wallMetaData));
    }
  }, [wallMetaData, dimensionStatus]);

  useEffect(() => {
    triggerEscape();
  }, [dimensionStatus]);

  const showSection = (): JSX.Element | null => {
    if (actionType === actionTypes.addWall) {
      return (
        <AddWall restartFields={triggerEscape} selectedDot={selectedDot} />
      );
    }

    if (actionType === actionTypes.addWall2Connections) {
      return <AddWall2Connections selectedDot={selectedDot} />;
    }

    if (actionType === actionTypes.addConnectionByWallLine) {
      return (
        <AddConnectionByWallLine
          actionTriggeredCallback={() => {
            setActionType(actionTypes.addWall);
          }}
        />
      );
    }

    if (actionType === actionTypes.addConnectionByDelta) {
      return <AddConnectionByDelta setActionType={setActionType} />;
    }

    if (actionType === actionTypes.reattachconnections) {
      return <ReAttachConnections setActionType={setActionType} />;
    }

    if (dimensionStatus === THREE_D && actionType === actionTypes.downloads) {
      return <Downloads />;
    }

    if (actionType === actionTypes.changeGlassMeetingSign) {
      return <ChangeGlassMeetingSign setActionType={setActionType} />;
    }

    return null;
  };

  const threeDOnlyActions = [actionTypes.downloads];

  useEffect(() => {
    const options = getOptions(
      [
        actionTypes.addConnectionByWallLine,
        actionTypes.addConnectionByDelta,
        actionTypes.addWall,
        actionTypes.addWall2Connections,
        actionTypes.changeGlassMeetingSign,
        actionTypes.downloads,
        actionTypes.reattachconnections,
      ]
        .filter(
          (action) =>
            dimensionStatus === THREE_D || !threeDOnlyActions.includes(action)
        )
        .filter(
          (action) =>
            dimensionStatus !== THREE_D || threeDOnlyActions.includes(action)
        )
        .filter(
          (action) =>
            action !== actionTypes.downloads || configurationId !== undefined
        ),
      translations.common,
      'availableActions'
    );

    setActionOptions(options);
    setActionType(
      dimensionStatus === THREE_D ? actionTypes.downloads : actionTypes.addWall
    );
  }, [dimensionStatus]);

  const showGlassWallModal =
    selectedWall &&
    isGlassWall(selectedWall) &&
    wall !== null &&
    wallMetaData !== null &&
    dimensionStatus === THREE_D &&
    actionType !== actionTypes.reattachconnections;

  return (
    <>
      {showGlassWallModal && glassAreas !== null ? (
        <GlassWallModal
          closeModalNotSaved={closeModalNotSaved}
          glassAreas={glassAreas}
          restartFields={closeModal}
          setGlassAreas={setGlassAreas}
          wallCoordinates={wallCoordinates}
          hasWritePermissions={hasWritePermissions}
        />
      ) : null}
      {propertiesBarOpen ? (
        <div className="properties-bar-content">
          <h3>{t(`${translations.common}:Properties`).toLocaleUpperCase()}</h3>
          <Row>
            <Col>
              <SelectInputFieldControlled
                id="actionType"
                label={t(`${translations.common}:Action`)}
                onChange={handleActionType}
                name="actionType"
                required
                options={actionOptions}
                value={actionType}
              />
            </Col>
          </Row>
          {showSection()}
        </div>
      ) : (
        ''
      )}
    </>
  );
};

export default PropertiesBar;
